import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  Shield, ShieldCheck, Loader2, Settings, ChevronDown, ChevronRight,
  LayoutDashboard, CalendarDays, Tags, Barcode, FileSearch,
  Layers, Scissors, Tag, HardDrive, Trash2,
  Truck, Database, ClipboardList, FileText, Package, Users,
} from 'lucide-react';

const MENU_PERMISSIONS = {
  operations: {
    label: 'Operations',
    icon: Truck,
    pages: {
      dashboard: { path: '/', label: 'Dashboard', icon: LayoutDashboard },
      sales_order_entry: { path: '/sales-order/new', label: 'Sales Order Entry', icon: ClipboardList },
      order_pipeline: { path: '/planner', label: 'Order Pipeline', icon: CalendarDays },
      dispatches: { path: '/dispatches', label: 'Dispatches', icon: Truck },
      dispatch_view: { path: '/dispatch-view', label: 'Dispatch View', icon: FileText },
      new_dispatch: { path: '/dispatch/new', label: 'New Dispatch Note', icon: FileText },
    }
  },
  master_data: {
    label: 'Master Data',
    icon: Database,
    pages: {
      customers: { path: '/customers', label: 'Customers', icon: Users },
      cobranding: { path: '/cobranding', label: 'Co-Branding', icon: Tags },
      products: { path: '/products', label: 'Products', icon: Package },
      supplier_codes: { path: '/supplier-codes', label: 'Supplier Product Codes', icon: Barcode },
      supplier_trace: { path: '/supplier-trace', label: 'Customer Code Trace', icon: FileSearch },
      batches: { path: '/batches', label: 'Batches', icon: Layers },
    }
  },
  quality: {
    label: 'Quality & Transformation',
    icon: ShieldCheck,
    pages: {
      qc_management: { path: '/qc', label: 'QC Management', icon: ShieldCheck },
      transformations: { path: '/transformations', label: 'Transformations', icon: Scissors },
    }
  },
  documents: {
    label: 'Documents & Reports',
    icon: FileText,
    pages: {
      template_designer: { path: '/templates', label: 'Template Designer', icon: Tag },
      reports: { path: '/reports', label: 'Reports', icon: FileText },
    }
  },
  administration: {
    label: 'Administration',
    icon: Settings,
    pages: {
      user_management: { path: '/users', label: 'User Management', icon: Shield },
      deleted_items: { path: '/deleted-items', label: 'Deleted Items', icon: Trash2 },
      data_management: { path: '/admin/data-management', label: 'Data Management', icon: Database },
      label_reprints: { path: '/admin/label-reprints', label: 'Label Reprints', icon: FileText },
      backup_restore: { path: '/backup', label: 'Backup & Restore', icon: HardDrive },
      settings: { path: '/settings', label: 'Settings', icon: Settings },
    }
  }
};

const PERMISSION_CATEGORIES_UI = {
  orders: { label: 'Orders & Dispatches', icon: Truck },
  batches: { label: 'Batches & QC', icon: Package },
  products: { label: 'Products & Customers', icon: ClipboardList },
  labels: { label: 'Labels', icon: FileText },
  reports: { label: 'Reports & Export', icon: FileText },
  admin: { label: 'Admin & Settings', icon: Settings },
};

export function PermissionsDialog({
  open,
  onOpenChange,
  editingUser,
  userPermissions,
  setUserPermissions,
  pagePermissions,
  setPagePermissions,
  systemPermissions,
  permissionCategories,
  saving,
  onSave,
}) {
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const expanded = {};
    Object.keys(MENU_PERMISSIONS).forEach(cat => { expanded[cat] = true; });
    return expanded;
  });

  const togglePermission = (permKey) => {
    setUserPermissions(prev => ({ ...prev, [permKey]: !prev[permKey] }));
  };

  const togglePagePermission = (pageKey) => {
    setPagePermissions(prev => ({ ...prev, [pageKey]: !prev[pageKey] }));
  };

  const toggleCategoryPages = (categoryKey, checked) => {
    const category = MENU_PERMISSIONS[categoryKey];
    if (!category) return;
    setPagePermissions(prev => {
      const updated = { ...prev };
      Object.keys(category.pages).forEach(pageKey => { updated[pageKey] = checked; });
      return updated;
    });
  };

  const isCategoryFullySelected = (categoryKey) => {
    const category = MENU_PERMISSIONS[categoryKey];
    if (!category) return false;
    return Object.keys(category.pages).every(pageKey => pagePermissions[pageKey]);
  };

  const isCategoryPartiallySelected = (categoryKey) => {
    const category = MENU_PERMISSIONS[categoryKey];
    if (!category) return false;
    const pageKeys = Object.keys(category.pages);
    const selectedCount = pageKeys.filter(pageKey => pagePermissions[pageKey]).length;
    return selectedCount > 0 && selectedCount < pageKeys.length;
  };

  const toggleCategoryExpanded = (categoryKey) => {
    setExpandedCategories(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
  };

  const selectAllPages = () => {
    const allPages = {};
    Object.values(MENU_PERMISSIONS).forEach(category => {
      Object.keys(category.pages).forEach(pageKey => { allPages[pageKey] = true; });
    });
    setPagePermissions(allPages);
  };

  const clearAllPages = () => setPagePermissions({});

  const countSelectedPages = () => Object.values(pagePermissions).filter(v => v).length;

  const countTotalPages = () =>
    Object.values(MENU_PERMISSIONS).reduce((total, cat) => total + Object.keys(cat.pages).length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Manage Permissions - {editingUser?.name}
          </DialogTitle>
          <DialogDescription>
            Configure page access and permissions for this user.
            {editingUser?.role === 'super_admin' && (
              <span className="text-amber-600 font-medium ml-2">This user is a Super Admin with full access.</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="pages" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="pages">Page Access</TabsTrigger>
            <TabsTrigger value="actions">Action Permissions</TabsTrigger>
          </TabsList>

          <TabsContent value="pages">
            <div className="flex items-center justify-between mb-4 p-2 bg-muted/30 rounded">
              <span className="text-sm text-muted-foreground">
                {countSelectedPages()} of {countTotalPages()} pages selected
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={selectAllPages} disabled={editingUser?.role === 'super_admin'}>
                  Select All
                </Button>
                <Button size="sm" variant="outline" onClick={clearAllPages} disabled={editingUser?.role === 'super_admin'}>
                  Clear All
                </Button>
              </div>
            </div>

            <ScrollArea className="h-[45vh] pr-4">
              <div className="space-y-2">
                {Object.entries(MENU_PERMISSIONS).map(([categoryKey, category]) => {
                  const CategoryIcon = category.icon;
                  const isExpanded = expandedCategories[categoryKey];
                  const isFullySelected = isCategoryFullySelected(categoryKey);
                  const isPartiallySelected = isCategoryPartiallySelected(categoryKey);

                  return (
                    <div key={categoryKey} className="border rounded-lg overflow-hidden">
                      <div
                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 ${
                          isFullySelected ? 'bg-emerald-50' : isPartiallySelected ? 'bg-amber-50' : 'bg-muted/20'
                        }`}
                        onClick={() => toggleCategoryExpanded(categoryKey)}
                      >
                        <button className="p-0.5">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        <Checkbox
                          checked={editingUser?.role === 'super_admin' || isFullySelected}
                          ref={(el) => {
                            if (el && isPartiallySelected && !isFullySelected) {
                              el.dataset.state = 'indeterminate';
                            }
                          }}
                          onCheckedChange={(checked) => toggleCategoryPages(categoryKey, checked)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={editingUser?.role === 'super_admin'}
                          className={isPartiallySelected && !isFullySelected ? 'data-[state=indeterminate]:bg-amber-500' : ''}
                        />
                        <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium text-sm flex-1">{category.label}</span>
                        <Badge variant="secondary" className="text-xs">
                          {Object.keys(category.pages).filter(k => pagePermissions[k]).length}/{Object.keys(category.pages).length}
                        </Badge>
                      </div>

                      {isExpanded && (
                        <div className="border-t bg-white">
                          {Object.entries(category.pages).map(([pageKey, page]) => {
                            const PageIcon = page.icon;
                            const isSelected = editingUser?.role === 'super_admin' || pagePermissions[pageKey];

                            return (
                              <div
                                key={pageKey}
                                className={`flex items-center gap-3 p-2.5 pl-12 border-b last:border-b-0 hover:bg-muted/30 ${
                                  isSelected ? 'bg-emerald-50/50' : ''
                                }`}
                              >
                                <Checkbox
                                  id={`page-${pageKey}`}
                                  checked={isSelected}
                                  onCheckedChange={() => togglePagePermission(pageKey)}
                                  disabled={editingUser?.role === 'super_admin'}
                                />
                                <PageIcon className="w-4 h-4 text-muted-foreground" />
                                <label htmlFor={`page-${pageKey}`} className="text-sm cursor-pointer flex-1">
                                  {page.label}
                                </label>
                                <span className="text-xs text-muted-foreground font-mono">{page.path}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="actions">
            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-6">
                {Object.entries(permissionCategories).map(([catKey, permKeys]) => {
                  const category = PERMISSION_CATEGORIES_UI[catKey] || { label: catKey, icon: Settings };
                  const CategoryIcon = category.icon;

                  return (
                    <div key={catKey} className="space-y-3">
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                        <h4 className="font-medium text-sm">{category.label}</h4>
                      </div>

                      <div className="grid gap-2">
                        {permKeys.map(permKey => (
                          <div
                            key={permKey}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              userPermissions[permKey] ? 'bg-emerald-50 border-emerald-200' : 'bg-muted/30'
                            }`}
                          >
                            <div className="space-y-0.5">
                              <label htmlFor={`perm-${permKey}`} className="text-sm font-medium cursor-pointer">
                                {systemPermissions[permKey] || permKey.replace(/_/g, ' ').replace(/can /i, '')}
                              </label>
                              <p className="text-xs text-muted-foreground">{permKey}</p>
                            </div>
                            <Switch
                              id={`perm-${permKey}`}
                              checked={editingUser?.role === 'super_admin' || userPermissions[permKey] || false}
                              onCheckedChange={() => togglePermission(permKey)}
                              disabled={editingUser?.role === 'super_admin'}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {countSelectedPages()} pages, {Object.values(userPermissions).filter(Boolean).length} action permissions
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving || editingUser?.role === 'super_admin'}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Save Permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
